// permissions.js - 權限常量定義

/**
 * 權限範圍常量
 */
export const PERMISSION_SCOPES = {
    NONE: 0,     // 無權限
    OWN: 1,      // 只能存取自己的資源
    ALL: 2       // 可以存取所有資源
};

/**
 * 權限分類常量
 */
export const PERMISSION_CATEGORIES = {
    CALENDAR: 'Calendar',
    LISTINGS: 'Listings',
    REPORTS: 'Reports',
    RESERVATIONS: 'Reservations',
    REVIEWS: 'Reviews',
    USERS: 'Users'
};

/**
 * 權限名稱常量
 * 根據您提供的 JWT Token 中的權限結構定義
 */
export const PERMISSIONS = {
    // ========== Users 使用者管理 ==========
    USERS_READ: 'Users.Read',
    USERS_CREATE: 'Users.Create',
    USERS_EDIT: 'Users.Edit',
    USERS_DELETE: 'Users.Delete',
    USERS_MANAGE_LISTING: 'Users.ManageListing',
    USERS_MANAGE_ROLES: 'Users.ManageRoles',
    USERS_MANAGE_PERMISSIONS: 'Users.ManagePermissions',
    USERS_SYNC: 'Users.Sync',

    // ========== Listings 房源管理 ==========
    LISTINGS_READ: 'Listings.Read',
    LISTINGS_CREATE: 'Listings.Create',
    LISTINGS_EDIT: 'Listings.Edit',
    LISTINGS_DELETE: 'Listings.Delete',
    LISTINGS_VIEW_PRICES: 'Listings.ViewPrices',
    LISTINGS_EDIT_PRICES: 'Listings.EditPrices',
    LISTINGS_SYNC: 'Listings.Sync',

    // ========== Reservations 預訂管理 ==========
    RESERVATIONS_READ: 'Reservations.Read',
    RESERVATIONS_CREATE: 'Reservations.Create',
    RESERVATIONS_EDIT: 'Reservations.Edit',
    RESERVATIONS_CANCEL: 'Reservations.Cancel',
    RESERVATIONS_SYNC: 'Reservations.Sync',

    // ========== Calendar 行事曆管理 ==========
    CALENDAR_READ: 'Calendar.Read',
    CALENDAR_EDIT: 'Calendar.Edit',
    CALENDAR_SYNC: 'Calendar.Sync',

    // ========== Reports 報表查詢 ==========
    REPORTS_VIEW: 'Reports.View',
    REPORTS_EXPORT: 'Reports.Export',

    // ========== Reviews 評價管理 ==========
    REVIEWS_READ: 'Reviews.Read',
    REVIEWS_MANAGE: 'Reviews.Manage',
    REVIEWS_SYNC: 'Reviews.Sync',

    // ========== System 系統管理 ==========
    SYSTEM_MANAGE_SETTINGS: 'System.ManageSettings',
    SYSTEM_VIEW_LOGS: 'System.ViewLogs',
    SYSTEM_MANAGE_WEBHOOKS: 'System.ManageWebhooks',
    SYSTEM_FULL_ACCESS: 'System.FullAccess'
};

/**
 * 角色常量
 */
export const ROLES = {
    OWNER: 'Owner',
    OWNER_WITH_EDIT_PRICES: 'OwnerWithEditPrices',
    ADMIN: 'Admin',
    MANAGER: 'Manager',
    USER: 'User'
};

/**
 * 權限檢查輔助函數
 */
export const PermissionHelper = {
    /**
     * 檢查是否為管理員角色
     * @param {string[]} userRoles 
     * @returns {boolean}
     */
    isAdmin: (userRoles) => {
        return userRoles?.includes(ROLES.ADMIN) || userRoles?.includes(ROLES.OWNER);
    },

    /**
     * 檢查是否為擁有者角色
     * @param {string[]} userRoles 
     * @returns {boolean}
     */
    isOwner: (userRoles) => {
        return userRoles?.includes(ROLES.OWNER);
    },

    /**
     * 根據權限範圍判斷權限描述
     * @param {number} scope 
     * @returns {string}
     */
    getScopeDescription: (scope) => {
        switch (scope) {
            case PERMISSION_SCOPES.NONE:
                return 'No Access';
            case PERMISSION_SCOPES.OWN:
                return 'Own Only';
            case PERMISSION_SCOPES.ALL:
                return 'All Access';
            default:
                return 'Unknown';
        }
    },

    /**
     * 取得權限的友好顯示名稱
     * @param {string} permissionName 
     * @returns {string}
     */
    getPermissionDisplayName: (permissionName) => {
        const displayNames = {
            [PERMISSIONS.CALENDAR_READ]: 'View Calendar',
            [PERMISSIONS.LISTINGS_READ]: 'View Listings',
            [PERMISSIONS.REPORTS_VIEW]: 'View Reports',
            [PERMISSIONS.REPORTS_EXPORT]: 'Export Reports',
            [PERMISSIONS.RESERVATIONS_READ]: 'View Reservations',
            [PERMISSIONS.REVIEWS_READ]: 'View Reviews',
            [PERMISSIONS.USERS_READ]: 'View Users',
            [PERMISSIONS.USERS_EDIT]: 'Edit Users'
        };
        return displayNames[permissionName] || permissionName;
    }
};

export default {
    PERMISSION_SCOPES,
    PERMISSION_CATEGORIES,
    PERMISSIONS,
    ROLES,
    PermissionHelper
};